import { z } from 'zod';

/**
 * ObraFit Module 2 · Custom checklists.
 *
 * Light counterpart to FVS. PMs design their own field-typed forms
 * (yes/no, text, number, choice, photo, date) and fill them once.
 * Submissions snapshot the template at create time so later template
 * edits don't rewrite past responses.
 */

export const checklistFieldTypeSchema = z.enum([
  'yes_no', 'text', 'numeric', 'single_choice', 'multi_choice', 'photo', 'date',
]);
export type ChecklistFieldType = z.infer<typeof checklistFieldTypeSchema>;

export const checklistFieldSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().trim().min(1).max(240),
  type: checklistFieldTypeSchema,
  required: z.boolean().default(false),
  helpText: z.string().trim().max(400).nullable().optional(),
  options: z.array(z.string().trim().min(1)).optional(),
  unit: z.string().trim().max(20).nullable().optional(),
});
export type ChecklistField = z.infer<typeof checklistFieldSchema>;

export const checklistAnswerSchema = z.object({
  fieldId: z.string(),
  value: z.union([
    z.string(), z.number(), z.boolean(), z.array(z.string()), z.null(),
  ]),
});
export type ChecklistAnswer = z.infer<typeof checklistAnswerSchema>;

export const checklistTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  fields: z.array(checklistFieldSchema),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChecklistTemplate = z.infer<typeof checklistTemplateSchema>;

export const createChecklistTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(600).nullable().optional(),
  fields: z.array(checklistFieldSchema).min(1, 'Adicione ao menos um campo'),
});
export type CreateChecklistTemplateRequest = z.infer<typeof createChecklistTemplateRequestSchema>;

export const updateChecklistTemplateRequestSchema = createChecklistTemplateRequestSchema.partial();
export type UpdateChecklistTemplateRequest = z.infer<typeof updateChecklistTemplateRequestSchema>;

export const checklistStatusSchema = z.enum(['aberta', 'concluida']);
export type ChecklistStatus = z.infer<typeof checklistStatusSchema>;

export const checklistSubmissionSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  templateId: z.string().nullable(),
  templateName: z.string(),
  fields: z.array(checklistFieldSchema),
  answers: z.array(checklistAnswerSchema),
  status: checklistStatusSchema,
  stageId: z.string().nullable(),
  stageName: z.string().nullable(),
  completedBy: z.string().nullable(),
  completedAt: z.string().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChecklistSubmission = z.infer<typeof checklistSubmissionSchema>;

export const createSubmissionRequestSchema = z.object({
  projectId: z.string().min(1),
  templateId: z.string().min(1),
  stageId: z.string().nullable().optional(),
  notes: z.string().max(600).nullable().optional(),
});
export type CreateSubmissionRequest = z.infer<typeof createSubmissionRequestSchema>;

export const saveAnswersRequestSchema = z.object({
  answers: z.array(checklistAnswerSchema),
  complete: z.boolean().default(false),
});
export type SaveAnswersRequest = z.infer<typeof saveAnswersRequestSchema>;
